import React, { useState, useEffect, useCallback } from "react";
import useApi from "../../utils/useApi";
import CrudManager from "../../utils/Crud-Manager/CrudManager";
import schema from "./Data/TransactionCRUDConfig.json";
import PageHeader from "../../components/PageHeader";
import { toast } from 'react-toastify';
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import CsvUploader from "../manage/CsvUploader";
const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

function TransactionPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDataFetched, setIsDataFetched] = useState(false);
  const { Get, getHost } = useApi();

  // Debounced search function
  const debounceSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchValue) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (!searchValue.trim()) {
            setFilteredData(data);
            return;
          }
          const filtered = data.filter(item => 
            item.user_full_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
            item.investment_id?.toString().includes(searchValue) ||
            item.user_phone_no?.toString().includes(searchValue) ||
            item.group_title?.toLowerCase().includes(searchValue.toLowerCase())
          );
          setFilteredData(filtered);
        }, 300);
      };
    })()
  , [data]);

  useEffect(() => {
    debounceSearch(searchTerm);
  }, [searchTerm, debounceSearch]);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const fetchData = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    try {
      // Convert dates to DD/MM/YYYY format
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);

      const response = await Get(
        getHost() +
          `/api/v1/getTransactionInRange?start_date=${formattedStartDate}&end_date=${formattedEndDate}`
      );
      setData(response);
      setFilteredData(response);
      setIsDataFetched(true);
      toast.success('Transaction data fetched successfully');
    } catch (error) {
      console.error("Failed to fetch data", error);
      toast.error('Failed to fetch transaction data. Please try again.');
    }
  };
  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    try {
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);

      const response = await Get(
        `${getHost()}/api/v1/exportToCsv?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
        { responseType: "blob" } // Ensure response type is blob for file download
      );

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${startDate}_TO_${endDate}_ALL_MOBILE_TRXN.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Data exported successfully');
    } catch (error) {
      console.error("Failed to export data", error);
      toast.error('Failed to export data. Please try again.');
    }
  };

  const handleDayCloseExport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    try {
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(endDate);

      const response = await Get(
        `${getHost()}/api/v1/exportDayCloseToCsv?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
        { responseType: "blob" } // Ensure response type is blob for file download
      );

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${startDate}_TO_${endDate}_NON_EXPO_MOBILE_TRXN.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Day close data exported successfully');
    } catch (error) {
      console.error("Failed to export data", error);
      toast.error('Failed to export day close data. Please try again.');
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          marginTop: 0,
          marginLeft: 15,
          marginRight: 15,
          marginBottom: 15,
        }}
      >
        <PageHeader pageTitle="Manage Your Transaction" />
        <Box sx={{ display: "flex", flexDirection: "column", marginTop: 2 }}>
          <Box
            sx={{
              marginTop: 1,
              marginBottom: 1,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              variant="standard"
              sx={{ width: "200px" }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <TextField
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              variant="standard"
              sx={{ width: "200px" }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#17202A", // Button background color
                  color: "#fff", // Text color
                  marginRight: 1,
                  width: "150px",
                  "&:hover": {
                    backgroundColor: "#1C2833", // Darker shade for hover effect
                  },
                }}
                onClick={fetchData}
              >
                Get
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#17202A", // Button background color
                  color: "#fff", // Text color
                  marginRight: 0,
                  width: "150px",
                  "&:hover": {
                    backgroundColor: "#1C2833", // Darker shade for hover effect
                  },
                }}
                onClick={handleExport}
              >
                Export
              </Button>
              &nbsp;&nbsp;
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "orange", // Button background color

                  color: "black", // Text color
                  marginRight: 0,
                  width: "auto",
                  "&:hover": {
                    backgroundColor: "white", // Darker shade for hover effect
                  },
                }}
                onClick={handleDayCloseExport}
              >
                Day Close Export
              </Button>
              {isDataFetched && data.length === 0 && (
                <Typography
                  variant="h6"
                  sx={{
                    marginLeft: 2,
                    color: "red",
                    fontWeight: "bold",
                  }}
                >
                  No data found
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {data.length > 0 && (
          <Box sx={{ marginTop: 2, marginBottom: 2 }}>
            <TextField
              label="Search transactions..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, phone, investment ID, or group"
              sx={{ width: '400px' }}
            />
          </Box>
        )}

        {filteredData.length > 0 && (
          <TableContainer
            component={Paper}
            sx={{ marginTop: 2, marginBottom: 1 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#17202A" }}>
                  <TableCell sx={{ color: "#fff" }}>User Full Name</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Investment ID</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Amount</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Penalty</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Transaction At</TableCell>
                  <TableCell sx={{ color: "#fff" }}>User Phone No</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Group Title</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Transaction Fee</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Subscription No</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.user_full_name}</TableCell>
                    <TableCell>{row.investment_id}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.penalty}</TableCell>
                    <TableCell>{row.transaction_at}</TableCell>
                    <TableCell>{row.user_phone_no}</TableCell>
                    <TableCell>{row.group_title}</TableCell>
                    <TableCell>{row.transaction_fee}</TableCell>
                    <TableCell>{row.subscription_no}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <CrudManager
          data={schema}
          actionBtnName="Add New Transaction"
          createFormTitle="Add Transaction"
          updateFormTitle="Update Transaction"
        />
      </Box>
    </Box>
  );
}

export default TransactionPage;
